import { useEffect } from 'react'

import type { NavSelection } from './nav-types'

interface UseClickawayProps {
  current: NavSelection
  clear: () => void
  // The mobile sheet owns its own dismissal (scrim tap, drag, Escape), so the
  // desktop click-away must not fire there: a press on something outside the
  // sheet, like a site popup, would otherwise reset it to its front screen.
  enabled: boolean
}

const useClickaway = ({ current, clear, enabled }: UseClickawayProps) => {
  // Handle escape key and outside clicks to close nav
  useEffect(() => {
    if (!enabled) {
      return undefined
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && current !== null) {
        clear()
      }
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (!current.containerId) {
        return
      }

      const target = e.target as HTMLElement
      const clickaway = target.closest('[data-menu-clickaway]')

      // If click is outside the nav, close it. Clicks inside any element marked with
      // data-menu-clickaway are treated as inside-the-panel and don't close the nav.
      if (!clickaway) {
        clear()
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [current, clear, enabled])
}
export default useClickaway
